import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  MessageSquare,
  FileText,
  Database,
  GitBranch,
  BookOpen,
  ListTodo,
  Plus,
  AtSign,
  Search,
  Upload,
  Download,
  RefreshCw,
  Gavel,
  Calendar,
  ChevronRight,
  CheckCircle2,
  Info,
  Coins,
  FileEdit,
  ClipboardList,
  FileOutput,
  Bookmark,
  ShieldCheck,
  Network,
  PenLine,
  PanelLeftClose,
  PanelLeftOpen,
  ScanSearch,
  Sparkles,
  X,
  ExternalLink
} from 'lucide-react';
import logoIcon from '@/assets/loop-icon.png';

interface UserGuideProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserGuide({ open, onOpenChange }: UserGuideProps) {
  const [activeSection, setActiveSection] = useState<string>('overview');
  const [navOpen, setNavOpen] = useState(true);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const content = useRef<HTMLDivElement | null>(null);

  /**
   * The guide's contents, in groups.
   *
   * Fourteen entries in one flat list gave no sense of what the platform is made of — the
   * pages you work in, the three tools that stand on their own, the forms, the account —
   * and finding anything meant reading all of it. `keywords` are what the search box
   * matches besides the name, so "103" finds Examination and "prior art" finds ForTech.
   */
  const GROUPS: {
    label: string;
    items: { id: string; name: string; icon: typeof Info; keywords?: string }[];
  }[] = [
    {
      label: 'Start here',
      items: [{ id: 'overview', name: 'Overview', icon: Info, keywords: 'start getting started platform privacy' }],
    },
    {
      label: 'Working pages',
      items: [
        { id: 'chat', name: 'Chat', icon: MessageSquare, keywords: 'ask questions ai model citations' },
        { id: 'documents', name: 'Documents', icon: FileText, keywords: 'patents publications uploads onboard' },
        { id: 'collections', name: 'Collections', icon: Bookmark, keywords: 'groups sets organise' },
        { id: 'drafts', name: 'Drafts', icon: FileEdit, keywords: 'editor write text' },
        { id: 'uspto', name: 'USPTO Data', icon: Database, keywords: 'application file wrapper status pair patent center' },
        { id: 'examination', name: 'Examination', icon: GitBranch, keywords: 'office action rejection 102 103 112 prosecution' },
        { id: 'resources', name: 'Resources', icon: BookOpen, keywords: 'mpep cfr usc rules law' },
        { id: 'tasks', name: 'Tasks', icon: ListTodo, keywords: 'parts list claim support reporting email ocr translate' },
      ],
    },
    {
      label: 'Tools',
      items: [
        { id: 'fortech', name: 'ForTech', icon: Network, keywords: 'corpus prior art landscape novelty inventive step concepts glossary search' },
        { id: 'drafting', name: 'Drafting', icon: PenLine, keywords: 'application specification claims figures numerals disclosure draft' },
        { id: 'action', name: 'Office action', icon: Gavel, keywords: 'response remarks amendments rejections shell matter' },
      ],
    },
    {
      label: 'Filings & forms',
      items: [
        { id: 'ids', name: 'IDS', icon: ClipboardList, keywords: 'information disclosure statement sb08 references cited' },
        { id: 'forms', name: 'USPTO Forms', icon: Download, keywords: 'rce terminal disclaimer notice of appeal pre-filled' },
        { id: 'oa_templates', name: 'OA Templates', icon: FileOutput, keywords: 'response shell docx firm template' },
      ],
    },
    {
      label: 'Your account',
      items: [
        { id: 'credits', name: 'Credits & Pricing', icon: Coins, keywords: 'cost plan quota billing' },
        { id: 'privacy', name: 'Privacy', icon: ShieldCheck, keywords: 'confidential training data security' },
      ],
    },
  ];

  const sections = GROUPS.flatMap(group => group.items);

  const needle = query.trim().toLowerCase();
  const matches = useMemo(
    () =>
      GROUPS.map(group => ({
        ...group,
        items: group.items.filter(
          item =>
            !needle ||
            item.name.toLowerCase().includes(needle) ||
            (item.keywords || '').includes(needle)
        ),
      })).filter(group => group.items.length > 0),
    // GROUPS is rebuilt each render and is constant in content; the search depends on the
    // needle alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [needle]
  );

  const activeName = sections.find(item => item.id === activeSection)?.name ?? 'Overview';

  /** A new section starts at its own beginning, not halfway down the last one. */
  useEffect(() => {
    if (content.current) content.current.scrollTop = 0;
  }, [activeSection]);

  /** Leave the guide for the tool it describes. */
  const goToTool = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-[800px] md:max-w-[1000px] lg:max-w-[1200px] xl:max-w-[1400px] h-[90vh] p-0 flex flex-col gap-0">
        <div className="flex flex-1 overflow-hidden w-full">
          {/* Contents.
              Grouped and searchable, and foldable away: on a second reading the prose is
              what is wanted. Hidden entirely below `md`, where a 14rem column beside the
              text leaves no room for either — there the picker in the bar above does the
              same job. */}
          <div
            className={`${
              navOpen ? 'hidden md:flex' : 'hidden'
            } w-56 border-r bg-gray-50 flex-col flex-shrink-0 overflow-hidden`}
          >
            <div className="p-4 border-b">
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <img src={logoIcon} alt="Patent Loop" className="h-8 w-auto" />
                  <DialogTitle className="text-lg">User Guide</DialogTitle>
                </div>
              </DialogHeader>
            </div>

            <div className="border-b p-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                <input
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  placeholder="Search the guide"
                  aria-label="Search the guide"
                  className="w-full rounded-md border border-gray-300 bg-white py-1.5 pl-8 pr-7 text-sm
                             outline-none focus:border-blue-400"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-700"
                    aria-label="Clear the search"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            <ScrollArea className="flex-1 min-h-0 p-3">
              <div className="space-y-4">
                {matches.map(group => (
                  <div key={group.label} className="space-y-1">
                    <p className="px-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      {group.label}
                    </p>
                    {group.items.map(section => {
                      const Icon = section.icon;
                      return (
                        <button
                          key={section.id}
                          onClick={() => setActiveSection(section.id)}
                          aria-current={activeSection === section.id ? 'true' : undefined}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                            activeSection === section.id
                              ? 'bg-blue-100 text-blue-700 font-medium'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <Icon className="w-4 h-4 shrink-0" />
                          <span className="truncate text-left">{section.name}</span>
                        </button>
                      );
                    })}
                  </div>
                ))}
                {matches.length === 0 && (
                  <p className="px-3 text-sm text-gray-500">Nothing matches “{query}”.</p>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Content Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* The bar above the text: what you are reading, the fold, and — where there is
                no room for the column — the whole of the navigation as one picker. */}
            <div className="flex items-center gap-2 border-b px-3 py-2">
              <button
                type="button"
                onClick={() => setNavOpen(value => !value)}
                className="hidden rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-900 md:block"
                title={navOpen ? 'Hide the contents' : 'Show the contents'}
                aria-label={navOpen ? 'Hide the contents' : 'Show the contents'}
                aria-expanded={navOpen}
              >
                {navOpen ? (
                  <PanelLeftClose className="h-4 w-4" />
                ) : (
                  <PanelLeftOpen className="h-4 w-4" />
                )}
              </button>
              <span className="hidden text-sm font-medium text-gray-900 md:inline">
                {activeName}
              </span>
              <select
                value={activeSection}
                onChange={event => setActiveSection(event.target.value)}
                aria-label="Go to a section of the guide"
                className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm
                           font-medium text-gray-900 md:hidden"
              >
                {GROUPS.map(group => (
                  <optgroup key={group.label} label={group.label}>
                    {group.items.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div ref={content} className="flex-1 min-h-0 overflow-y-auto">
              <div className="max-w-3xl p-5 sm:p-8">
                {activeSection === 'overview' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">Patent Loop User Guide</h2>
                      <p className="text-gray-700 mb-4">
                        Patent Loop is two things at once. There are the <strong>working pages</strong>
                        {' '}— chat, documents, USPTO data, examination, resources, tasks — where you
                        read, ask and analyse. And there are three <strong>tools</strong> that each
                        take one job end to end: <strong>ForTech</strong> for reading a technical
                        field, <strong>Drafting</strong> for writing an application, and{' '}
                        <strong>Office action</strong> for answering one.
                      </p>
                      <p className="text-sm text-gray-600">
                        Each tool opens in its own workspace and carries a <strong>?</strong> in its
                        bar with its own step-by-step guide. This guide is the map of the whole
                        platform.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">The three tools</h3>
                      <div className="space-y-3">
                        <button
                          type="button"
                          onClick={() => setActiveSection('fortech')}
                          className="flex w-full items-start gap-3 rounded-lg bg-slate-50 p-3 text-left transition hover:bg-slate-100"
                        >
                          <Network className="mt-0.5 h-5 w-5 text-slate-700" />
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900">ForTech</div>
                            <div className="text-sm text-gray-600">
                              Read a whole technical field: a corpus of documents, mapped by
                              concept, problem and solution, with novelty and inventive step
                              worked against it
                            </div>
                          </div>
                          <ChevronRight className="ml-auto mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveSection('drafting')}
                          className="flex w-full items-start gap-3 rounded-lg bg-slate-50 p-3 text-left transition hover:bg-slate-100"
                        >
                          <PenLine className="mt-0.5 h-5 w-5 text-slate-700" />
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900">Drafting</div>
                            <div className="text-sm text-gray-600">
                              A disclosure to a filed specification — contributions, claims,
                              figures, reference numerals, the detailed description, the checks
                            </div>
                          </div>
                          <ChevronRight className="ml-auto mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveSection('action')}
                          className="flex w-full items-start gap-3 rounded-lg bg-slate-50 p-3 text-left transition hover:bg-slate-100"
                        >
                          <Gavel className="mt-0.5 h-5 w-5 text-slate-700" />
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900">Office action</div>
                            <div className="text-sm text-gray-600">
                              A rejection to the response that answers it — rejections, strategy,
                              amendments, remarks, and your firm&rsquo;s shell filled
                            </div>
                          </div>
                          <ChevronRight className="ml-auto mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                        </button>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">The working pages</h3>
                      <div className="space-y-3">
                        <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                          <MessageSquare className="w-5 h-5 text-blue-600 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900">Chat</div>
                            <div className="text-sm text-gray-600">AI-powered patent analysis and Q&A</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                          <FileText className="w-5 h-5 text-purple-600 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900">Documents</div>
                            <div className="text-sm text-gray-600">Manage patents, publications, and uploads</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                          <Database className="w-5 h-5 text-green-600 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900">USPTO Data</div>
                            <div className="text-sm text-gray-600">View and manage USPTO applications, and download populated USPTO forms</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg">
                          <GitBranch className="w-5 h-5 text-orange-600 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900">Examination</div>
                            <div className="text-sm text-gray-600">Office action analysis and prosecution workflow</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg">
                          <BookOpen className="w-5 h-5 text-yellow-600 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900">Resources</div>
                            <div className="text-sm text-gray-600">Access MPEP, CFR, and USC reference materials</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-pink-50 rounded-lg">
                          <ListTodo className="w-5 h-5 text-pink-600 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900">Tasks</div>
                            <div className="text-sm text-gray-600">Run various tasks, such as for generating parts lists, Office Action reporting emails, finding support for claims, etc.</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-teal-50 rounded-lg">
                          <ClipboardList className="w-5 h-5 text-teal-600 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900">IDS</div>
                            <div className="text-sm text-gray-600">Generate USPTO-compliant Information Disclosure Statement forms with automatic patent data retrieval</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-indigo-50 rounded-lg">
                          <Download className="w-5 h-5 text-indigo-600 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900">Forms</div>
                            <div className="text-sm text-gray-600">Generate pre-filled USPTO forms (RCE, Terminal Disclaimer, Notice of Appeal, etc.) for multiple applications at once</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                          <FileOutput className="w-5 h-5 text-red-600 mt-0.5" />
                          <div>
                            <div className="font-medium text-gray-900">OA Templates</div>
                            <div className="text-sm text-gray-600">Upload your OA response shell document and generate filled responses with application data, claims, and remarks</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Where to start</h3>
                      <p className="mb-3 text-sm text-gray-600">
                        By what you are trying to do, rather than by what the platform contains.
                      </p>
                      <div className="divide-y rounded-lg border bg-white">
                        {([
                          ['I want to ask something about a patent', 'Add it on Documents, then ask on Chat', 'documents'],
                          ['I am writing a new application', 'Drafting — start from notes, or import a draft you have', 'drafting'],
                          ['I have an Office action to answer', 'Office action — one matter per application', 'action'],
                          ['I need to know what is already out there', 'ForTech — build a corpus of the field and read it together', 'fortech'],
                          ['I am checking a rejection against the art', 'Examination for the action; ForTech for what the field teaches', 'examination'],
                          ['I need a form, an IDS, or a response shell', 'USPTO Forms, IDS, or OA Templates', 'forms'],
                          ['I want to look up a rule', 'Resources — the MPEP, the CFR and the USC', 'resources'],
                        ] as [string, string, string][]).map(([want, where, id]) => (
                          <button
                            key={want}
                            type="button"
                            onClick={() => setActiveSection(id)}
                            className="flex w-full items-start gap-3 px-4 py-2.5 text-left transition hover:bg-gray-50"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900">{want}</div>
                              <div className="text-xs text-gray-600">{where}</div>
                            </div>
                            <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-gray-400" />
                          </button>
                        ))}
                      </div>
                    </div>

                    <Separator />

                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-emerald-600" />
                        Privacy & Confidentiality
                      </h3>
                      <p className="text-sm text-gray-700 mb-2">
                        Your data is always confidential. All documents, messages, and information you provide are kept strictly private and are <strong>never used for AI model training</strong>.
                      </p>
                      <p className="text-sm text-gray-700">
                        Feedback and usage data are only accessed by Patent Loop personnel for the sole purpose of improving the application. Your intellectual property and communications remain secure and confidential at all times.
                      </p>
                    </div>
                  </div>
                )}

                {activeSection === 'chat' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <MessageSquare className="inline w-6 h-6 mr-2 mb-1 text-blue-600" />
                        Chat Page
                      </h2>
                      <p className="text-gray-700">
                        The Chat page provides AI-powered analysis and question-answering capabilities for your patent documents.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Features</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Plus className="w-4 h-4 text-blue-600" />
                            Adding Documents
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Click the <strong>+ Add Documents</strong> button to add patents, publications, or applications to your session.
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Search by application number, patent number, or publication number</li>
                            <li>Upload PDF files directly</li>
                            <li>Documents are stored in your session for easy access</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <AtSign className="w-4 h-4 text-blue-600" />
                            Mentions
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Type <code className="bg-gray-100 px-1 rounded">@</code> in the chat to mention specific documents:
                          </p>
                          <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm">
                            <code>@US10123456 What is the main invention?</code>
                          </div>
                          <p className="text-xs text-gray-600 mt-2">
                            This ensures the AI focuses on the specific document you mention.
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <MessageSquare className="w-4 h-4 text-blue-600" />
                            Asking Questions
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">Example questions you can ask:</p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>"What are the independent claims?"</li>
                            <li>"Summarize the invention in simple terms"</li>
                            <li>"What prior art is cited?"</li>
                            <li>"Compare claim 1 with claim 5"</li>
                            <li>"What is the filing date and status?"</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-blue-900 mb-1">Pro Tip</h4>
                          <p className="text-sm text-blue-800">
                            Be specific in your questions for better results. Instead of "Tell me about this patent,"
                            try "What problem does this invention solve?"
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'documents' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <FileText className="inline w-6 h-6 mr-2 mb-1 text-purple-600" />
                        Documents Page
                      </h2>
                      <p className="text-gray-700">
                        The Documents page is your central hub for managing all patents, publications, applications, and uploaded files.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Document Types</h3>
                      <div className="space-y-3">
                        <div className="border-l-4 border-blue-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Patents</div>
                          <div className="text-sm text-gray-600">Granted US patents with full text and metadata</div>
                        </div>
                        <div className="border-l-4 border-green-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Publications</div>
                          <div className="text-sm text-gray-600">Published US patent applications</div>
                        </div>
                        <div className="border-l-4 border-orange-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Applications</div>
                          <div className="text-sm text-gray-600">USPTO applications with prosecution history</div>
                        </div>
                        <div className="border-l-4 border-cyan-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Foreign Patents</div>
                          <div className="text-sm text-gray-600">International patents from JP, EP, WO, and other jurisdictions</div>
                        </div>
                        <div className="border-l-4 border-purple-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Uploads</div>
                          <div className="text-sm text-gray-600">Your uploaded PDF documents (Pro/Premium only)</div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Features</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Search className="w-4 h-4 text-purple-600" />
                            Search and Filter
                          </h4>
                          <p className="text-sm text-gray-700">
                            Use the search bar to find documents by number, title, inventor, or assignee.
                            Filter by document type to narrow your results.
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Upload className="w-4 h-4 text-purple-600" />
                            Upload Documents
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Click <strong>Add Documents</strong> to upload PDF files or fetch documents from USPTO:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Drag and drop PDF files or click to browse</li>
                            <li>Enter patent, publication, or application numbers</li>
                            <li>Documents are processed and added to your session</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-purple-600" />
                            View Document Details
                          </h4>
                          <p className="text-sm text-gray-700">
                            Click on any document to view its full details, including claims, description,
                            drawings, and metadata.
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Download className="w-4 h-4 text-purple-600" />
                            Download
                          </h4>
                          <p className="text-sm text-gray-700">
                            Use the three-dot menu on each document to download the PDF version.
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Bookmark className="w-4 h-4 text-purple-600" />
                            Collections
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Organize documents into named collections for quick access. Use the <strong>Manage Collections</strong> button
                            (bookmark icon) next to the collection filter dropdown, or filter by collection using the dropdown.
                            See the <strong>Collections</strong> section of this guide for full details.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        <Calendar className="inline w-5 h-5 mr-1 mb-1 text-blue-600" />
                        Daily Onboarding Limits
                      </h3>
                      <p className="text-sm text-gray-700 mb-4">
                        To ensure fair usage and system performance, Patent Loop implements daily limits for document onboarding.
                        These limits reset every day at midnight (UTC).
                      </p>

                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Patents, Publications & Foreign Patents</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            US Patents, US Publications, and Foreign Patents (JP, EP, WO, etc.) all count toward the same daily limit:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li><strong>Free Tier:</strong> 10 documents per day</li>
                            <li><strong>Pro Tier ($19/mo):</strong> 25 documents per day</li>
                            <li><strong>Premium Tier ($47/mo):</strong> 100 documents per day</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Document Uploads</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Uploaded documents have a separate daily limit and use credits based on file size:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li><strong>Free Tier:</strong> Not available</li>
                            <li><strong>Pro Tier ($19/mo):</strong> 10 uploads per day</li>
                            <li><strong>Premium Tier ($47/mo):</strong> 25 uploads per day + OCR capability</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Batch Onboarding</h4>
                          <p className="text-sm text-gray-700">
                            When using the <strong>Batch Mode</strong> (accessible from the Add Documents button),
                            you can onboard multiple documents at once. The system processes 3 documents simultaneously
                            until all are complete or your daily limit is reached. If you hit your limit during batch
                            processing, you'll see a notification and remaining documents won't be processed.
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Viewing Your Usage</h4>
                          <p className="text-sm text-gray-700">
                            You can track your daily onboarding usage in the <strong>Credits & Usage</strong> modal
                            (accessible from the user menu in the header). This shows:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Current usage vs. daily limit for each category</li>
                            <li>Remaining quota for today</li>
                            <li>Percentage of daily limit used</li>
                            <li>Comparison of limits across subscription tiers</li>
                          </ul>
                        </div>

                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                          <div className="flex items-start gap-2">
                            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-900">
                              <strong>Note:</strong> Daily limits only apply to onboarding new documents.
                              Once a document is onboarded, you can access, analyze, and use it unlimited times
                              without consuming additional quota.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'collections' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <Bookmark className="inline w-6 h-6 mr-2 mb-1 text-amber-600" />
                        Collections
                      </h2>
                      <p className="text-gray-700">
                        Collections let you group related documents together so you can quickly find and load them
                        into any session. Think of them as reusable folders for patents, publications, uploads, and
                        foreign patents.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">What Is a Collection?</h3>
                      <p className="text-sm text-gray-700 mb-3">
                        A collection is a named list of document references that persists across sessions. Unlike
                        session documents (which belong to a single session), collections are always available and
                        can be used to quickly load a set of documents into any session.
                      </p>
                      <div className="space-y-3">
                        <div className="border-l-4 border-amber-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Persistent</div>
                          <div className="text-sm text-gray-600">Collections are saved to your account and available across all sessions</div>
                        </div>
                        <div className="border-l-4 border-blue-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Any Document Type</div>
                          <div className="text-sm text-gray-600">Add patents, publications, applications, foreign patents, and uploads to the same collection</div>
                        </div>
                        <div className="border-l-4 border-green-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Quick Loading</div>
                          <div className="text-sm text-gray-600">Load all documents from a collection into your active session with a few clicks</div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Creating a Collection</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">From the Collections Manager</h4>
                          <ol className="text-sm text-gray-700 list-decimal list-inside ml-4 space-y-1">
                            <li>Open the Collections Manager using the bookmark icon on the Documents toolbar, or choose Manage documents and collections from the sidebar Session documents menu.</li>
                            <li>Type a name in the input field at the bottom</li>
                            <li>Click <strong>Create</strong></li>
                          </ol>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">From Session Documents</h4>
                          <ol className="text-sm text-gray-700 list-decimal list-inside ml-4 space-y-1">
                            <li>Open the menu beside Session documents and choose Manage documents and collections.</li>
                            <li>In the <strong>Active Docs</strong> tab, select one or more documents</li>
                            <li>Click <strong>Add to Collection</strong> and choose an existing collection or create a new one</li>
                          </ol>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Managing Collections</h3>
                      <p className="text-sm text-gray-700 mb-3">
                        Open the Collections Manager to browse, edit, and organize your collections.
                      </p>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <ChevronRight className="w-4 h-4 text-amber-600" />
                            Browse Contents
                          </h4>
                          <p className="text-sm text-gray-700">
                            Click on a collection to expand it and see all documents inside. Each document
                            shows a formatted name (e.g., "US Patent 9,864,572").
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-amber-600" />
                            Rename
                          </h4>
                          <p className="text-sm text-gray-700">
                            Click the three-dot menu on a collection and select <strong>Rename</strong>.
                            Type the new name and press Enter to save.
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Plus className="w-4 h-4 text-amber-600" />
                            Remove Documents
                          </h4>
                          <p className="text-sm text-gray-700">
                            Expand a collection and click the X button next to any document to remove it.
                            This does not delete the document itself — it only removes it from the collection.
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Search className="w-4 h-4 text-amber-600" />
                            Delete a Collection
                          </h4>
                          <p className="text-sm text-gray-700">
                            Click the three-dot menu and select <strong>Delete</strong>. A confirmation dialog
                            will appear. Deleting a collection does not delete the documents inside it.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Loading Documents from a Collection</h3>
                      <p className="text-sm text-gray-700 mb-3">
                        You can quickly add documents from a collection to your active session:
                      </p>
                      <ol className="text-sm text-gray-700 list-decimal list-inside ml-4 space-y-2">
                        <li>Click <strong>+ Add Documents</strong> in the sidebar or Documents page</li>
                        <li>Select <strong>From Collection</strong> as the document source</li>
                        <li>Expand any collection to see its documents</li>
                        <li>Use the checkboxes to select individual documents, or use <strong>Select All</strong> to pick every document in a collection</li>
                        <li>Documents already in your active session are marked with an "Active" badge</li>
                        <li>Click <strong>Add Documents to Session</strong> to load the selected documents</li>
                      </ol>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Where to Access Collections</h3>
                      <div className="space-y-3">
                        <div className="border-l-4 border-blue-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Chat Page — Sidebar</div>
                          <div className="text-sm text-gray-600">Open the Session documents menu and choose Manage documents and collections.</div>
                        </div>
                        <div className="border-l-4 border-purple-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Documents Page — Toolbar</div>
                          <div className="text-sm text-gray-600">Click the bookmark icon next to the collection filter dropdown to open the Collections Manager</div>
                        </div>
                        <div className="border-l-4 border-green-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Documents Page — Filter Dropdown</div>
                          <div className="text-sm text-gray-600">Filter the documents list to show only documents belonging to a specific collection</div>
                        </div>
                        <div className="border-l-4 border-orange-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Add Documents Modal</div>
                          <div className="text-sm text-gray-600">Select "From Collection" to browse and add collection documents to your active session</div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-amber-900 mb-1">Use Case Examples</h4>
                          <ul className="text-sm text-amber-800 space-y-1 mt-2">
                            <li>• <strong>Prior Art Research</strong> — Group all prior art references for a specific invention</li>
                            <li>• <strong>Client Portfolio</strong> — Keep all of a client's patents and applications together</li>
                            <li>• <strong>Office Action Response</strong> — Collect the cited references and your own patents for comparison</li>
                            <li>• <strong>Family Tracking</strong> — Group related patents, continuations, and foreign equivalents</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'drafts' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <FileEdit className="inline w-6 h-6 mr-2 mb-1 text-indigo-600" />
                        Drafts
                      </h2>
                      <div className="mb-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg">
                        <div className="flex items-center gap-2 text-purple-700 font-semibold mb-1">
                          <span className="text-lg">⭐</span>
                          <span>Premium Feature</span>
                        </div>
                        <p className="text-sm text-purple-600">
                          Drafts is available exclusively for Premium account holders. Upgrade your account to access draft creation, editing, and export features.
                        </p>
                      </div>
                      <p className="text-gray-700">
                        Create, edit, and manage patent draft documents with line-numbered editing and professional DOCX export capabilities.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Accessing Drafts</h3>
                      <p className="text-sm text-gray-700 mb-3">
                        You can access and work with drafts in three different ways:
                      </p>
                      <div className="space-y-3">
                        <div className="border-l-4 border-indigo-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Drafts Page</div>
                          <div className="text-sm text-gray-600">
                            Click the Drafts icon (📝) in the top navigation bar (next to notifications) to open the dedicated drafts workspace
                          </div>
                        </div>
                        <div className="border-l-4 border-blue-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Chat Interface</div>
                          <div className="text-sm text-gray-600">
                            Generate and edit draft content within the chat area
                          </div>
                        </div>
                        <div className="border-l-4 border-orange-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Examination Page</div>
                          <div className="text-sm text-gray-600">
                            Open drafts alongside office actions and other documents in the examination workspace
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Creating Drafts</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Plus className="w-4 h-4 text-indigo-600" />
                            Create from Drafts Page
                          </h4>
                          <ol className="text-sm text-gray-700 list-decimal list-inside ml-4 space-y-1">
                            <li>Click the <strong>Drafts icon</strong> in the top navigation</li>
                            <li>Click <strong>+ New Draft</strong> button</li>
                            <li>Enter a title for your draft</li>
                            <li>Select a draft type (General, Specification, Claims, Background, Summary, Description)</li>
                            <li>Click <strong>Create Draft</strong></li>
                          </ol>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <MessageSquare className="w-4 h-4 text-indigo-600" />
                            Generate from Chat
                          </h4>
                          <p className="text-sm text-gray-700">
                            Ask the AI to generate draft content (e.g., "Draft claims for..."), then save the generated content as a draft.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Draft Features</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <FileEdit className="w-4 h-4 text-indigo-600" />
                            Line-Numbered Editor
                          </h4>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Every line displays a line number on the left for easy reference</li>
                            <li>Click line numbers to jump to specific lines</li>
                            <li>Professional layout similar to legal/patent document editors</li>
                            <li>Real-time character and word count</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Download className="w-4 h-4 text-indigo-600" />
                            Export to DOCX
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Export drafts to Microsoft Word format:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Click <strong>Export DOCX</strong> button in Drafts Page</li>
                            <li>Or click <strong>Export</strong> icon when viewing in Examination Page</li>
                            <li>File downloads automatically with proper formatting</li>
                            <li>Includes title, draft type, metadata, and full content</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <GitBranch className="w-4 h-4 text-indigo-600" />
                            Multi-Document Integration
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            In the Examination Page, open drafts alongside other documents:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li><strong>Side-by-side:</strong> Draft + Office Action for claim amendments</li>
                            <li><strong>Three-way:</strong> Draft + Prior Art + Office Action for comparison</li>
                            <li><strong>Multi-draft:</strong> Work on Spec + Claims simultaneously</li>
                            <li>Arrange in horizontal, vertical, or grid layouts</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Draft Types</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                          <div className="font-medium text-sm text-gray-900">General</div>
                          <div className="text-xs text-gray-600">Freeform drafting for any purpose</div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                          <div className="font-medium text-sm text-gray-900">Specification</div>
                          <div className="text-xs text-gray-600">Patent specification sections</div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                          <div className="font-medium text-sm text-gray-900">Claims</div>
                          <div className="text-xs text-gray-600">Independent and dependent claims</div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                          <div className="font-medium text-sm text-gray-900">Background</div>
                          <div className="text-xs text-gray-600">Background of the invention</div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                          <div className="font-medium text-sm text-gray-900">Summary</div>
                          <div className="text-xs text-gray-600">Summary of the invention</div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                          <div className="font-medium text-sm text-gray-900">Description</div>
                          <div className="text-xs text-gray-600">Detailed description sections</div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Managing Drafts</h3>
                      <div className="space-y-3">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-1 text-sm">Viewing All Drafts</h4>
                          <p className="text-sm text-gray-600">
                            The Drafts Page shows all your drafts in a list with title, content preview, type badge, and last updated date.
                          </p>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 mb-1 text-sm">Editing</h4>
                          <p className="text-sm text-gray-600">
                            Click any draft to open it for editing. Changes must be saved manually using the <strong>Save</strong> button.
                          </p>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 mb-1 text-sm">Deleting</h4>
                          <p className="text-sm text-gray-600">
                            Hover over a draft in the list and click the trash icon (🗑️) to delete it permanently.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Best Practices</h3>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-gray-700">
                            <strong>Use descriptive titles</strong> - Include application number or invention name
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-gray-700">
                            <strong>Save frequently</strong> - Click Save after significant changes (no auto-save)
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-gray-700">
                            <strong>Export regularly</strong> - Keep DOCX backups for external review
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-gray-700">
                            <strong>Use multi-document view</strong> - Open drafts with related documents in Examination Page
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-gray-700">
                            <strong>Leverage line numbers</strong> - Use for navigation and referencing specific sections
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-indigo-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-indigo-900 mb-1">Workflow Tip</h4>
                          <p className="text-sm text-indigo-800">
                            When responding to office actions, open your draft alongside the office action in the Examination Page.
                            This allows you to make amendments while viewing rejections, creating a more efficient workflow than
                            switching between separate pages.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'uspto' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <Database className="inline w-6 h-6 mr-2 mb-1 text-green-600" />
                        USPTO Data Page
                      </h2>
                      <p className="text-gray-700">
                        View and manage USPTO patent applications with detailed information and prosecution documents.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Features</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Database className="w-4 h-4 text-green-600" />
                            Application List
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            The left panel shows all applications in your session with:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Status badges (Granted, Pending, Abandoned)</li>
                            <li>Application number and title</li>
                            <li>Search functionality to find specific applications</li>
                            <li>Filter by status</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <RefreshCw className="w-4 h-4 text-green-600" />
                            Refresh Application Data
                          </h4>
                          <p className="text-sm text-gray-700">
                            Click the <strong>Refresh</strong> button to fetch the latest data from USPTO,
                            including new prosecution documents and status updates.
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-green-600" />
                            Application Details
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            The main panel displays comprehensive information:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Filing date, inventors, assignee, and examiner</li>
                            <li>Current status and patent/publication numbers</li>
                            <li>Bibliographic data</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Download className="w-4 h-4 text-green-600" />
                            Prosecution Documents
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            View and download all prosecution documents:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Office actions and responses</li>
                            <li>Claims, specifications, and drawings</li>
                            <li>Notices and correspondence</li>
                            <li>Color-coded by document type</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Download className="w-4 h-4 text-green-600" />
                            Export to Excel
                          </h4>
                          <p className="text-sm text-gray-700">
                            Export application data to Excel for reporting and analysis.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'examination' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <GitBranch className="inline w-6 h-6 mr-2 mb-1 text-orange-600" />
                        Examination Page
                      </h2>
                      <p className="text-gray-700">
                        The Examination page is your workspace for analyzing office actions and managing prosecution workflows.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Layout Overview</h3>
                      <div className="space-y-3">
                        <div className="border border-gray-200 rounded-lg p-3">
                          <div className="font-medium text-gray-900 mb-1">Left Panel: Application Details</div>
                          <div className="text-sm text-gray-600">
                            Select an application and view its metadata, filing information, and status
                          </div>
                        </div>
                        <div className="border border-gray-200 rounded-lg p-3">
                          <div className="font-medium text-gray-900 mb-1">Center Panel: Document Viewers</div>
                          <div className="text-sm text-gray-600">
                            View up to 4 documents simultaneously (office actions, responses, specs, references)
                          </div>
                        </div>
                        <div className="border border-gray-200 rounded-lg p-3">
                          <div className="font-medium text-gray-900 mb-1">Right Panel: Analysis Results</div>
                          <div className="text-sm text-gray-600">
                            Display AI-powered analysis results and task outputs
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Features</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <RefreshCw className="w-4 h-4 text-orange-600" />
                            Refresh Application
                          </h4>
                          <p className="text-sm text-gray-700">
                            Click <strong>Refresh</strong> in the application details header to fetch the latest
                            data from USPTO, including new office actions and prosecution documents.
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-orange-600" />
                            Document Selection
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Select documents from the dropdown menus:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li><strong>Office Action:</strong> Select examiner actions and rejections</li>
                            <li><strong>Pros Doc:</strong> Select specifications, claims, drawings, and responses</li>
                            <li><strong>Reference:</strong> Add patents, publications, or legal references</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Gavel className="w-4 h-4 text-orange-600" />
                            Analysis Tools
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Click <strong>Analysis</strong> to run AI-powered tasks:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li><strong>Extract Office Action Info:</strong> Extract key dates, rejections, and requirements</li>
                            <li><strong>Analyze Issues:</strong> Detailed analysis of each rejection and claim</li>
                            <li><strong>Summarize Prior Art:</strong> Summarize cited references</li>
                            <li><strong>Generate OA Reporting Email:</strong> Create client reporting emails</li>
                            <li><strong>Build Timeline:</strong> Create prosecution history timeline</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-orange-600" />
                            Task History
                          </h4>
                          <p className="text-sm text-gray-700">
                            Click <strong>View History</strong> in the analysis panel to see all previous
                            analysis results for the current document or application.
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <BookOpen className="w-4 h-4 text-orange-600" />
                            Reference Documents
                          </h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Add reference documents for comparison:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Search for patents or publications</li>
                            <li>Browse MPEP sections</li>
                            <li>Browse CFR and USC sections</li>
                            <li>View your uploaded documents</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-orange-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-orange-900 mb-1">Workflow Tip</h4>
                          <p className="text-sm text-orange-800">
                            Start with "Extract Office Action Info" to get an overview, then use "Analyze Issues"
                            for detailed claim-by-claim analysis. Use "Generate OA Reporting Email" to create
                            client communications.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'resources' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <BookOpen className="inline w-6 h-6 mr-2 mb-1 text-yellow-600" />
                        Resources Page
                      </h2>
                      <p className="text-gray-700">
                        Access legal reference materials including MPEP, CFR, and USC for patent prosecution research.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Available Resources</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">MPEP (Manual of Patent Examining Procedure)</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Browse or search the complete MPEP:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Navigate by chapter and section</li>
                            <li>Search for specific section numbers (e.g., "2141")</li>
                            <li>Full text with formatting preserved</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">CFR (Code of Federal Regulations)</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Access Title 37 - Patents, Trademarks, and Copyrights:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Browse by part and subpart</li>
                            <li>Search for specific sections (e.g., "1.111")</li>
                            <li>View regulation text and requirements</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">USC (United States Code)</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Reference Title 35 - Patents:
                          </p>
                          <ul className="text-sm text-gray-600 list-disc list-inside ml-4 space-y-1">
                            <li>Browse patent statutes by section</li>
                            <li>Search for specific sections (e.g., "103")</li>
                            <li>View statutory language</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">How to Use</h3>
                      <ol className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-yellow-600 mt-0.5">1.</span>
                          <span>Select a resource type from the tabs (MPEP, CFR, or USC)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-yellow-600 mt-0.5">2.</span>
                          <span>Enter a section number in the search box or browse the table of contents</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-yellow-600 mt-0.5">3.</span>
                          <span>Click on a section to view its full text</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-yellow-600 mt-0.5">4.</span>
                          <span>Use the reference in the Examination page for side-by-side comparison</span>
                        </li>
                      </ol>
                    </div>
                  </div>
                )}

                {activeSection === 'tasks' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <ListTodo className="inline w-6 h-6 mr-2 mb-1 text-pink-600" />
                        Tasks Page
                      </h2>
                      <p className="text-gray-700">
                        Tasks are guided workflows that package proven prompts and output layouts to generate consistent patent deliverables with a few clicks.
                        Each task is optimized for specific patent prosecution needs.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Document Analysis Tasks</h3>
                      <div className="space-y-3">
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Parts List</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Automatically generates a comprehensive list of parts or components identified in a patent document.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Quickly identify all reference numerals and their corresponding components for specification writing or claim drafting.
                          </p>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Specification Map</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Maps specification details to relevant parts and features, creating connections between described elements and drawing references.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Ensure complete specification support for all claim elements and identify gaps in description.
                          </p>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Claims Support</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Analyzes claims for specification support, verifying that all claim elements have adequate written description.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Verify 35 U.S.C. § 112(a) written description compliance before filing or during prosecution.
                          </p>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Add Numerals to Claims</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Automatically adds reference numerals to claims that correspond to parts or components shown in the drawings.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Prepare claims with reference numerals for patent applications per 37 CFR 1.75(d)(2).
                          </p>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Custom Analysis</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Run custom analysis on patent documents with user-defined prompts for specialized research needs.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Perform specialized analysis not covered by standard tasks, such as freedom-to-operate analysis or technical feature extraction.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Invention Record Tasks</h3>
                      <div className="space-y-3">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">IR Metadata Extraction</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Extracts metadata from invention records including inventors, assignees, filing dates, and technical classifications.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Quickly populate application data sheets (ADS) and organize invention disclosure information.
                          </p>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">IR Innovation Analysis</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Analyzes innovation aspects and technological advancements described in invention records.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Identify patentable subject matter and distinguish novel features from conventional elements.
                          </p>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">IR Technology Classification</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Classifies invention records into appropriate technology categories and suggests relevant CPC/USPC classifications.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Determine proper patent classifications for prior art searching and application routing.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Patent Drafting Tasks</h3>
                      <div className="space-y-3">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Draft Detailed Description</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Generates comprehensive detailed description sections from invention records, user input, and context documents.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Create well-structured detailed descriptions that satisfy enablement and written description requirements.
                          </p>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Draft Claims</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Generates patent claims from disclosure text with proper claim formatting, dependencies, and structure.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Draft independent and dependent claims with proper antecedent basis and claim differentiation.
                          </p>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Draft Summary</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Generates summary of the invention section from disclosure text, highlighting key features and advantages.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Create concise summaries that introduce the invention and its benefits.
                          </p>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Draft Background</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Generates background section describing the technical field and problems addressed by the invention.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Draft background sections that establish context without admitting prior art.
                          </p>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Draft Other Embodiments</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Generates alternative embodiments and variations based on the primary invention description.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Expand claim scope by describing variations, modifications, and alternative implementations.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Office Action Tasks</h3>
                      <div className="space-y-3">
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Extract Office Action Info</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Extracts key information from office actions including deadlines, rejections, objections, and requirements.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Quickly identify critical deadlines and action items from lengthy office actions.
                          </p>
                        </div>

                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Analyze Office Action Issues</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Provides detailed claim-by-claim analysis of all rejections, objections, and examiner positions.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Understand examiner's reasoning and identify response strategies for each rejection.
                          </p>
                        </div>

                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Summarize Office Action Prior Art</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Summarizes all prior art references cited in the office action with relevant teachings and figures.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Quickly understand cited references and identify distinguishing features.
                          </p>
                        </div>

                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Build Office Action Timeline</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Creates a chronological timeline of the prosecution history from office action documents.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Track prosecution progress and understand the examination history at a glance.
                          </p>
                        </div>

                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Generate OA Reporting Email</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Generates professional email reports summarizing office action information for client communication.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Create clear, client-friendly summaries of office actions with next steps and deadlines.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Document Processing Tasks</h3>
                      <div className="space-y-3">
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Translate Document</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Translates entire patent documents or technical documents between languages while preserving technical accuracy and legal terminology.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Translate foreign patents for prior art analysis or prepare translations for PCT filings.
                          </p>
                        </div>

                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">LaTeX to Word Converter</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Converts LaTeX documents (ZIP archives) to Microsoft Word format while preserving mathematical equations and formatting.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Convert technical documents with complex equations to Word for USPTO filing requirements.
                          </p>
                        </div>

                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">PDF OCR</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            Extracts text from scanned PDF documents using optical character recognition. Output as text, Word (DOCX), or JSON with positional data.
                          </p>
                          <p className="text-xs text-gray-500">
                            <strong>Use case:</strong> Convert scanned documents to searchable, editable text for analysis and processing.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">How to Use Tasks</h3>
                      <ol className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-pink-600 mt-0.5">1.</span>
                          <span>Select a task from the left panel or use the search and filter to find specific tasks</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-pink-600 mt-0.5">2.</span>
                          <span>Fill in required inputs such as document selections, text input, or configuration options</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-pink-600 mt-0.5">3.</span>
                          <span>Click Submit to run the task - credit cost will be shown if significant</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-pink-600 mt-0.5">4.</span>
                          <span>View results in the task panel or access them later from task history</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-semibold text-pink-600 mt-0.5">5.</span>
                          <span>Add task results to Chat for further analysis or export to Word/PDF</span>
                        </li>
                      </ol>
                    </div>

                    <Separator />

                    <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-pink-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-pink-900 mb-1">Task History & Reusability</h4>
                          <p className="text-sm text-pink-800 mb-2">
                            All task results are saved in task history and can be reviewed at any time without additional credits.
                            You can also add previous task results to your chat for follow-up questions or refinements.
                          </p>
                          <p className="text-sm text-pink-800">
                            <strong>Tip:</strong> Review task history before re-running the same task to save credits.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'ids' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <ClipboardList className="inline w-6 h-6 mr-2 mb-1 text-blue-600" />
                        Information Disclosure Statement (IDS)
                      </h2>
                      <p className="text-gray-700">
                        Generate USPTO-compliant IDS forms automatically by entering patent references. Patent Loop fetches
                        patent data from the USPTO and EPO in order to populate IDS forms so they are ready for submission.
                      </p>
                    </div>

                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                        <Coins className="w-4 h-4 text-blue-600" />
                        Credit Costs
                      </h3>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-700">Free Users:</span>
                          <span className="text-gray-600">10 credits per IDS form</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-700">Pro/Premium Users:</span>
                          <span className="text-green-700 font-medium">0 credits (unlimited)</span>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Creating an IDS Form</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Step 1: Enter Application Information</h4>
                          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-2">
                            <li><strong>Application Number:</strong> Your USPTO application number (e.g., 17/123,456)</li>
                            <li><strong>Docket Number:</strong> Optional internal docket number</li>
                            <li><strong>IDS Name:</strong> Unique name for this IDS submission (required)</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Step 2: Add References</h4>
                          <p className="text-sm text-gray-700 mb-2">Enter patent references one per line in the appropriate sections:</p>
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                            <div>
                              <p className="font-medium text-sm text-gray-800 mb-1">US Patents/Publications</p>
                              <p className="text-xs text-gray-600 mb-1">Accepted formats:</p>
                              <ul className="text-xs text-gray-600 list-disc list-inside ml-2 space-y-0.5">
                                <li>US10123456 or 10,123,456 or US 10,123,456</li>
                                <li>US20190123456 or 2019/0123456 or 2019123456</li>
                              </ul>
                              <p className="text-xs text-gray-500 mt-1">Duplicates are automatically removed</p>
                            </div>
                            <div>
                              <p className="font-medium text-sm text-gray-800 mb-1">Foreign Patents</p>
                              <p className="text-xs text-gray-600">EP3456789A1, WO2020123456A1, JP2019123456A, etc.</p>
                            </div>
                            <div>
                              <p className="font-medium text-sm text-gray-800 mb-1">Non-Patent Literature (NPL)</p>
                              <p className="text-xs text-gray-600">Full citations for articles, papers, or other references</p>
                            </div>
                          </div>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Step 3: Select Signature</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Choose a saved signature to sign the IDS form. If no signatures are available, add one in Settings.
                          </p>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Step 4: Generate & Download</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Click "Generate IDS" to create your form. The system will:
                          </p>
                          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-2">
                            <li>Fetch patent data from the USPTO and/or EPO</li>
                            <li>Retrieve application information (filing date, examiner, etc.)</li>
                            <li>Sort references according to your preferences</li>
                            <li>Apply your signature</li>
                            <li>Generate a complete IDS PDF form</li>
                            <li>Automatically download the PDF</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Step 5: Download Reference PDFs (Optional)</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            After generating your IDS, you can download the actual patent PDFs for all references listed in the form.
                            Click the <strong>"Reference PDFs"</strong> button to fetch and package them:
                          </p>
                          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-2">
                            <li>US patents and publications are downloaded from USPTO</li>
                            <li>Foreign patents (EP, WO, etc.) are downloaded from Google Patents</li>
                            <li>If multiple references exist, they are packaged into a single ZIP file</li>
                            <li>If only one reference exists, the PDF is downloaded directly</li>
                            <li>Any references that could not be downloaded are listed in a <code className="text-xs bg-gray-100 px-1 rounded">_failed_downloads.txt</code> file inside the ZIP</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">IDS Settings</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Reference Sorting</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Configure how references are sorted in each section (US Patents, US Publications, Foreign Patents):
                          </p>
                          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-2">
                            <li><strong>Document Number:</strong> Sort by patent/publication number</li>
                            <li><strong>Input Order:</strong> Keep the order you entered them</li>
                            <li><strong>Date:</strong> Sort by publication/issue date</li>
                            <li><strong>Applicant/Patentee Name:</strong> Sort alphabetically by name</li>
                          </ul>
                          <p className="text-xs text-gray-600 mt-2">Each section can be sorted independently, ascending or descending.</p>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Docket Number Preference</h4>
                          <p className="text-sm text-gray-700 mb-2">Choose which docket number to use:</p>
                          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-2">
                            <li><strong>Use Retrieved Docket Number:</strong> Prefer USPTO data, fall back to your input</li>
                            <li><strong>Always Use Input Docket Number:</strong> Prioritize what you enter in the form</li>
                          </ul>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Signature Management</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Save multiple signatures for quick reuse:
                          </p>
                          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-2">
                            <li><strong>Name:</strong> Full name as it appears on the form</li>
                            <li><strong>Signature:</strong> How the signature appears (e.g., /John Doe/)</li>
                            <li><strong>Registration Number:</strong> USPTO registration number</li>
                            <li><strong>Label:</strong> Optional nickname (e.g., "Primary Attorney")</li>
                          </ul>
                          <p className="text-xs text-gray-600 mt-2">
                            Set a default signature to automatically select it for new IDS forms. Settings auto-save when you add, remove, or set a default signature.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">IDS History</h3>
                      <p className="text-sm text-gray-700 mb-3">
                        View all previously generated IDS forms with complete details:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-2">
                        <li>Application information (app number, docket, filing date, examiner, etc.)</li>
                        <li>Complete list of all references with dates and inventors/patentees</li>
                        <li>Search across all history (name, app numbers, patent numbers, inventors, etc.)</li>
                        <li>Re-download any previously generated IDS PDF</li>
                        <li>Download all reference PDFs for any past IDS via the <strong>"Ref PDFs"</strong> button</li>
                      </ul>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                        <p className="text-xs text-blue-800">
                          <strong>Tip:</strong> Use the search bar in History to quickly find IDS forms by application number,
                          docket number, patent references, or inventor names.
                        </p>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Tips & Best Practices</h3>
                      <div className="space-y-3">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4" />
                            Automatic Features
                          </h4>
                          <ul className="list-disc list-inside space-y-1 text-sm text-green-900 ml-2">
                            <li>Duplicates are automatically removed within each section</li>
                            <li>Patent numbers are normalized (formats like "US 10,123,456" and "10123456" are recognized as the same)</li>
                            <li>Publication numbers with missing zeros are automatically corrected</li>
                            <li>Application data (filing date, examiner, etc.) is automatically retrieved from USPTO</li>
                            <li>Generated IDS forms are automatically downloaded when ready</li>
                          </ul>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                            <Info className="w-4 h-4" />
                            Important Notes
                          </h4>
                          <ul className="list-disc list-inside space-y-1 text-sm text-yellow-900 ml-2">
                            <li>Always review the generated IDS form before submitting to USPTO</li>
                            <li>Verify that all references are correctly populated</li>
                            <li>Check that the signature and registration number are accurate</li>
                            <li>Ensure the application number and docket number are correct</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'forms' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <Download className="inline w-6 h-6 mr-2 mb-1 text-blue-600" />
                        USPTO Forms
                      </h2>
                      <p className="text-gray-700">
                        Generate pre-filled USPTO forms automatically using application data. Forms are populated with
                        information from the USPTO database and your saved signatures, ready for filing.
                      </p>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                        <Database className="w-4 h-4 text-blue-600" />
                        Two Ways to Generate Forms
                      </h3>
                      <div className="space-y-2 text-sm text-gray-700">
                        <p>
                          <strong>Forms Page (Batch):</strong> Use the <strong>Forms</strong> page in the main navigation
                          to generate forms for multiple applications at once. Enter multiple application numbers and
                          download all forms in a ZIP file.
                        </p>
                        <p>
                          <strong>USPTO Data Page (Single):</strong> Use the <strong>USPTO Data</strong> page to generate
                          forms for a single application. Load an application, then navigate to the <strong>Forms</strong> tab.
                        </p>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Forms Page - Batch Generation</h3>
                      <p className="text-sm text-gray-700 mb-4">
                        The Forms page allows you to generate the same form type for multiple applications at once,
                        saving significant time when you need to file similar forms across many cases.
                      </p>
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Step 1: Enter Application Numbers</h4>
                          <p className="text-sm text-gray-700">
                            Navigate to the Forms page from the main navigation. Enter one or more US application numbers
                            in the text area, either one per line or comma-separated.
                          </p>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Step 2: Select Form Type</h4>
                          <p className="text-sm text-gray-700">
                            Choose the type of form you want to generate from the dropdown. The same form type will
                            be generated for all entered applications.
                          </p>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Step 3: Select Signature (Optional)</h4>
                          <p className="text-sm text-gray-700">
                            Choose a saved signature to apply to all forms. Signatures are shared with the IDS feature
                            and can be managed in IDS Settings.
                          </p>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Step 4: Generate</h4>
                          <p className="text-sm text-gray-700">
                            Click "Generate" to create the forms. For a single application, you'll download a PDF directly.
                            For multiple applications, you'll receive a ZIP file containing all the PDFs.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">USPTO Data Page - Single Application</h3>
                      <p className="text-sm text-gray-700 mb-4">
                        For generating forms for a single application with more options (like Terminal Disclaimer reference numbers),
                        use the Forms tab in the USPTO Data page.
                      </p>
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Step 1: Load Application</h4>
                          <p className="text-sm text-gray-700">
                            Go to the USPTO Data page and load the application for which you want to generate forms.
                            The application data will be used to pre-fill the forms.
                          </p>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Step 2: Navigate to Forms Tab</h4>
                          <p className="text-sm text-gray-700">
                            Once the application is loaded, click on the <strong>Forms</strong> tab to access the
                            form generation panel.
                          </p>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Step 3: Select Form Type & Options</h4>
                          <p className="text-sm text-gray-700">
                            Choose the form you want to generate. Some forms have additional options
                            (e.g., Terminal Disclaimer type and reference number, POA customer number, Transmittal total pages).
                          </p>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Step 4: Generate & Download</h4>
                          <p className="text-sm text-gray-700">
                            Click "Generate & Download Form" to create and download the pre-filled PDF.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Available Forms</h3>
                      <div className="space-y-4">
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <h4 className="font-semibold text-gray-800 mb-2">RCE (Request for Continued Examination)</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Form SB/30 - Request for Continued Examination. Pre-filled with application number, filing date,
                            inventor name, art unit, examiner name, and docket number.
                          </p>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <h4 className="font-semibold text-gray-800 mb-2">IDS (Information Disclosure Statement)</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Form SB/08 - Blank IDS form with application data and signature only (no citations).
                            For full IDS with citations, use the dedicated IDS feature.
                          </p>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <h4 className="font-semibold text-gray-800 mb-2">Terminal Disclaimer</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Multiple Terminal Disclaimer forms available for different scenarios:
                          </p>
                          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-2">
                            <li><strong>Pending vs Pending:</strong> Pending application vs pending reference application</li>
                            <li><strong>Pending vs Prior Patent:</strong> Pending application vs prior issued patent</li>
                            <li><strong>Design Petition:</strong> Design application with petition under 37 CFR 1.137</li>
                            <li><strong>Patent vs Application:</strong> Issued patent vs pending application</li>
                            <li><strong>Patent vs Patent:</strong> Issued patent vs another issued patent</li>
                          </ul>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <h4 className="font-semibold text-gray-800 mb-2">Notice of Appeal</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Notice of Appeal to the Patent Trial and Appeal Board. Pre-filled with application information
                            including examiner and art unit.
                          </p>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <h4 className="font-semibold text-gray-800 mb-2">POA Transmittal</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            Power of Attorney Transmittal form. Includes optional customer number field for
                            correspondence address.
                          </p>
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <h4 className="font-semibold text-gray-800 mb-2">Transmittal Form</h4>
                          <p className="text-sm text-gray-700 mb-2">
                            General Transmittal form for USPTO filings. Includes optional total pages field.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Tips</h3>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-700">
                            <strong>Batch for efficiency:</strong> Use the Forms page when you need to generate the same
                            form type for multiple applications - it's much faster than doing them one at a time.
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-700">
                            <strong>Single for options:</strong> Use the USPTO Data page when you need form-specific options
                            like Terminal Disclaimer reference numbers or POA customer numbers.
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-700">
                            <strong>Review before filing:</strong> Always review the generated form before filing
                            to ensure all information is accurate and complete.
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-700">
                            <strong>Signatures:</strong> Set up your signatures once in the Form Settings and reuse them across
                            all forms. You can set a default signature for convenience.
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-700">
                            <strong>Date stamps:</strong> Forms are automatically dated with the current date
                            (Eastern Time) when generated.
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-700">
                            <strong>Manual edits:</strong> If needed, you can open the downloaded PDF in Adobe Acrobat
                            or another PDF editor to make additional edits before filing.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'oa_templates' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <FileOutput className="inline w-6 h-6 mr-2 mb-1 text-red-600" />
                        OA Response Templates
                      </h2>
                      <p className="text-gray-700">
                        Upload your firm's Office Action response shell document and generate a filled response
                        with application details, amended claims, and remarks sections populated automatically.
                      </p>
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                        <Info className="w-4 h-4 text-red-600" />
                        How It Works
                      </h3>
                      <div className="space-y-2 text-sm text-gray-700">
                        <p>
                          Patent Loop analyzes your uploaded shell document, identifies dynamic fields (dates, names,
                          serial numbers) and substantive sections (claims, remarks), then fills them with data from
                          the selected Office Action and application.
                        </p>
                        <p>
                          Your template's formatting — including fonts, headers, footers, signature blocks, and
                          paragraph spacing — is preserved in the generated document.
                        </p>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Getting Started</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Step 1: Upload Your Shell Document</h4>
                          <p className="text-sm text-gray-700">
                            Navigate to the <strong>OA Templates</strong> page from the header navigation. Upload a Word
                            (.docx) document that you use as a starting point for Office Action responses. This is
                            typically your firm's standard response shell with placeholder text for variable content.
                          </p>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Step 2: Review the Template</h4>
                          <p className="text-sm text-gray-700">
                            Patent Loop uses AI to analyze your document and identify dynamic fields and sections.
                            You can preview the generated template mapping to ensure all placeholders were identified
                            correctly.
                          </p>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-gray-800 mb-2">Step 3: Generate a Filled Response</h4>
                          <p className="text-sm text-gray-700">
                            Select an application number, the Office Action to respond to, and optionally a claims
                            document. Click <strong>Generate</strong> to produce a filled response document. The
                            generated Word file will be available for download.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">What Gets Filled</h3>
                      <div className="space-y-3">
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Dynamic Fields</h4>
                          <p className="text-sm text-gray-600">
                            Application serial number (formatted as XX/XXX,XXX), filing date, examiner name, art unit,
                            attorney information, and current date are automatically populated from application data.
                          </p>
                        </div>

                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Claims Section</h4>
                          <p className="text-sm text-gray-600">
                            Amended claims are inserted with proper formatting — claim numbering, status indicators
                            (e.g., "Previously Presented", "Cancelled"), and sub-element spacing are handled automatically.
                          </p>
                        </div>

                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Remarks Sections</h4>
                          <p className="text-sm text-gray-600">
                            Substantive remarks sections are populated based on the Office Action issues identified,
                            including responses to rejections under 35 U.S.C. 101, 102, 103, and 112.
                          </p>
                        </div>

                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <h4 className="font-medium text-gray-900 mb-1">Headers, Footers & Signature</h4>
                          <p className="text-sm text-gray-600">
                            Placeholders in headers and footers are filled too. The current date field is inserted as a
                            Word auto-updating date field. Signature block formatting (bold, line breaks) is preserved.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Tips</h3>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-700">
                            <strong>Use your existing shell:</strong> Upload the same Word document you already use
                            as a starting point — no special formatting or placeholders required.
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-700">
                            <strong>Upload claims separately:</strong> For best results with amended claims, upload
                            the latest claim set or select the claims document from prosecution history.
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-700">
                            <strong>Review and edit:</strong> The generated document is a first draft — review the
                            filled content, refine the arguments, and finalize before filing.
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-700">
                            <strong>Reuse templates:</strong> Your uploaded templates are saved and can be reused
                            across multiple applications and Office Actions.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* The three tools that stand on their own. Each is a route of its own with
                    its own app bar, and each carries a "?" in that bar with the full
                    step-by-step — so what belongs here is what the tool is for, when to
                    reach for it, and the shape of the work. */}
                {activeSection === 'fortech' && (
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center gap-2">
                        <Network className="h-6 w-6 text-slate-700" />
                        <h2 className="text-2xl font-bold text-gray-900">ForTech</h2>
                      </div>
                      <p className="mt-2 text-gray-700">
                        Reading a technical field, rather than one document. You gather the
                        documents that matter in a field into a <strong>corpus</strong>, and
                        ForTech reads all of them together — what each one is about, what
                        problems they address, how they solve them, and what vocabulary the
                        field uses for the same thing.
                      </p>
                    </div>

                    <Button size="sm" onClick={() => goToTool('/fortech')}>
                      Open ForTech
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </Button>

                    <Separator />

                    <div>
                      <h3 className="mb-3 text-lg font-semibold text-gray-900">When to reach for it</h3>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                          Before drafting, to see what is already out there and where the space
                          is.
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                          Answering a 103, to find what a field actually teaches rather than
                          what one examiner says it does.
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                          Coming to an unfamiliar art, where the first problem is the words.
                        </li>
                      </ul>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="mb-3 text-lg font-semibold text-gray-900">How it works</h3>
                      <ol className="space-y-3 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <span className="mt-0.5 font-semibold text-blue-600">1.</span>
                          <span>
                            <strong>Make a corpus</strong> and name the field it covers.
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-0.5 font-semibold text-blue-600">2.</span>
                          <span>
                            <strong>Add documents</strong> by number — patents, publications,
                            foreign documents — or search the web for candidates and add what
                            fits.
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-0.5 font-semibold text-blue-600">3.</span>
                          <span>
                            <strong>Let it read them.</strong> Concepts are extracted from each
                            document and tied back to the passage they came from, so every
                            finding can be checked against the text.
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-0.5 font-semibold text-blue-600">4.</span>
                          <span>
                            <strong>Work the corpus</strong> through its tabs: the technical
                            map, the findings, problems and solutions, the feature map, novelty
                            and inventive step, full-text and word search, the glossary, the
                            documents themselves, and collections for carving the set up.
                          </span>
                        </li>
                      </ol>
                    </div>

                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                      <p className="text-sm text-gray-700">
                        A corpus can be shared with a colleague, and it has a chat of its own
                        that answers from the documents in it. The <strong>?</strong> in
                        ForTech&rsquo;s own bar has the detail.
                      </p>
                    </div>
                  </div>
                )}

                {activeSection === 'drafting' && (
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center gap-2">
                        <PenLine className="h-6 w-6 text-slate-700" />
                        <h2 className="text-2xl font-bold text-gray-900">Drafting</h2>
                      </div>
                      <p className="mt-2 text-gray-700">
                        A disclosure to a filed specification, one confirmed step at a time.
                        Each step&rsquo;s output is the next one&rsquo;s input, nothing runs
                        ahead of you, and reference numerals are allocated in code rather than
                        by a model — so a numeral belongs to its element for the life of the
                        application.
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" onClick={() => goToTool('/drafting')}>
                        Open Drafting
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => goToTool('/drafting/reviews')}
                      >
                        <ScanSearch className="mr-2 h-4 w-4" />
                        Check an application
                      </Button>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="mb-3 text-lg font-semibold text-gray-900">The eleven steps</h3>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {[
                          ['Disclosure', 'Attach what it will be drafted from, each marked as the invention disclosure, background, or both.'],
                          ['Contributions', 'Settle what is new and what problem it solves.'],
                          ['Background', 'Written from the confirmed problems.'],
                          ['Claims', 'Proposed from the contributions, or paste your own and they are parsed unchanged.'],
                          ['Figures', 'Plan the figures — which is planning the detailed description.'],
                          ['Outline', 'The technology, and optionally the document in 37 CFR 1.77(b) order.'],
                          ['Confirm', 'The last cheap moment to change claims or figures, before numbering.'],
                          ['Detailed description', 'Per figure: number the elements, draw it, introduce it, discuss each element.'],
                          ['Abstract & summary', 'After the description, because both track the claims.'],
                          ['Review', 'Antecedent basis, claim support, numerals, the filing checklist.'],
                          ['Assemble', 'The specification, built on read so it is never stale.'],
                        ].map(([name, what], index) => (
                          <div key={name} className="rounded-lg border bg-white p-3">
                            <div className="text-sm font-medium text-gray-900">
                              {index + 1}. {name}
                            </div>
                            <div className="mt-0.5 text-xs text-gray-600">{what}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="mb-3 text-lg font-semibold text-gray-900">Worth knowing</h3>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                          <span>
                            <strong>Import a draft</strong> reads an application you already
                            have — claims, figures, parts list, paragraphs, numbering — so it
                            can be carried on here instead of retyped.
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                          <span>
                            The <strong>chat</strong> answers questions about the draft and
                            returns requested changes as proposals — every passage the change
                            touches, as a diff, applied only when you accept.
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                          <span>
                            <strong>Examples</strong> is your own library of saved passages,
                            followed when a matching paragraph is drafted — a firm&rsquo;s house
                            style, kept.
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                          <span>
                            <strong>Check an application</strong> runs the same review checks
                            against an upload, for drafts this tool did not build.
                          </span>
                        </li>
                      </ul>
                    </div>

                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                      <p className="text-sm text-gray-700">
                        The <strong>?</strong> in Drafting&rsquo;s own bar has every step in
                        detail, plus what each model setting does and how to ask for a model
                        that is not open to your account.
                      </p>
                    </div>
                  </div>
                )}

                {activeSection === 'action' && (
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center gap-2">
                        <Gavel className="h-6 w-6 text-slate-700" />
                        <h2 className="text-2xl font-bold text-gray-900">Office action</h2>
                      </div>
                      <p className="mt-2 text-gray-700">
                        A rejection to the response that answers it. One matter is one
                        application: you point it at the application, read the action, and work
                        forward through the rejections, the claims, the strategy, the amendments
                        and the remarks to a paper that can be filed.
                      </p>
                      <p className="mt-2 text-sm text-gray-600">
                        Distinct from the <strong>Examination</strong> page, which browses
                        prosecution and analyses an action. This one produces the response.
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" onClick={() => goToTool('/action')}>
                        Open Office action
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => goToTool('/action/templates')}
                      >
                        <FileOutput className="mr-2 h-4 w-4" />
                        Response templates
                      </Button>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="mb-3 text-lg font-semibold text-gray-900">The seven steps</h3>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {[
                          ['Application & action', 'Fetch the application, choose the action being answered, read the specification.'],
                          ['Rejections', 'Read the action: one entry per rejection, with its claims, statute and cited art.'],
                          ['Claims', 'The claims of record, with the status identifier each will carry.'],
                          ['Strategy', 'Argue, amend, or both — decided per rejection before anything is drafted.'],
                          ['Amendments', 'The claims as they will be filed, in your own wording.'],
                          ['Remarks', 'Each rejection outlined, then drafted from the outline you approved.'],
                          ['Response', 'Assembled on read, with the claim listing built in code to 37 CFR 1.121(c).'],
                        ].map(([name, what], index) => (
                          <div key={name} className="rounded-lg border bg-white p-3">
                            <div className="text-sm font-medium text-gray-900">
                              {index + 1}. {name}
                            </div>
                            <div className="mt-0.5 text-xs text-gray-600">{what}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="mb-3 text-lg font-semibold text-gray-900">Worth knowing</h3>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                          <span>
                            The Office action can be <strong>uploaded</strong> rather than taken
                            from the file wrapper — the USPTO&rsquo;s API withholds non-patent
                            literature and its own notifications.
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                          <span>
                            Your firm&rsquo;s <strong>response shell</strong> is filled from the
                            action and the claims, and is generated from the Claims step.
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                          <span>
                            The <strong>matter chat</strong> answers from everything the matter
                            holds and changes nothing — the work itself is the steps.
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                          <span>
                            Nothing is filed or signed for you: an S-signature has to be typed
                            by the person signing, under 37 CFR 1.4(d)(2).
                          </span>
                        </li>
                      </ul>
                    </div>

                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                      <p className="text-sm text-gray-700">
                        The <strong>?</strong> in the Office action bar has every step in
                        detail, the chat, and the model settings.
                      </p>
                    </div>
                  </div>
                )}

                {activeSection === 'credits' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <Coins className="inline w-6 h-6 mr-2 mb-1 text-blue-600" />
                        Credits & Pricing
                      </h2>
                      <p className="text-gray-700">
                        Patent Loop uses a credit-based system to provide fair and transparent pricing for AI-powered features.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">What Are Credits?</h3>
                      <p className="text-sm text-gray-700 mb-3">
                        Credits are the currency used within Patent Loop to access AI-powered analysis, document processing,
                        and other premium features. Each action that uses AI or computational resources consumes a certain
                        number of credits based on the complexity and resources required.
                      </p>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-blue-900 mb-1">Check Your Balance</h4>
                            <p className="text-sm text-blue-800">
                              Your current credit balance is always displayed in the header at the top right of the screen.
                              Click on it to view detailed usage history and purchase more credits.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">What Credits Are Used For</h3>
                      <div className="space-y-3">
                        <div className="border-l-4 border-purple-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">AI Chat Queries</div>
                          <div className="text-sm text-gray-600">
                            Each question you ask in the Chat page consumes credits based on the length and complexity
                            of the query and response
                          </div>
                        </div>
                        <div className="border-l-4 border-green-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Document Analysis</div>
                          <div className="text-sm text-gray-600">
                            Processing and analyzing patents, publications, and uploaded documents
                          </div>
                        </div>
                        <div className="border-l-4 border-orange-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Office Action Analysis</div>
                          <div className="text-sm text-gray-600">
                            Running tasks like Extract OA Info, Analyze Issues, Summarize Prior Art, and Generate Reporting Emails
                          </div>
                        </div>
                        <div className="border-l-4 border-blue-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Drafting Tasks</div>
                          <div className="text-sm text-gray-600">
                            Generating claims, specifications, parts lists, and other patent drafting content
                          </div>
                        </div>
                        <div className="border-l-4 border-pink-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Document Processing</div>
                          <div className="text-sm text-gray-600">
                            Loading and processing documents from USPTO, including fetching prosecution history
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Credit Costs</h3>
                      <p className="text-sm text-gray-700 mb-3">
                        Credit costs vary based on the complexity and computational resources required:
                      </p>
                      <ul className="text-sm text-gray-700 list-disc list-inside ml-4 space-y-2">
                        <li><strong>Simple queries:</strong> Fewer credits for basic questions or short responses</li>
                        <li><strong>Complex analysis:</strong> More credits for detailed analysis of office actions or multi-document comparisons</li>
                        <li><strong>Large documents:</strong> Processing longer documents consumes more credits</li>
                        <li><strong>Batch operations:</strong> Running multiple tasks or analyzing entire prosecution histories</li>
                      </ul>
                      <p className="text-sm text-gray-600 mt-3">
                        The exact credit cost is shown before running tasks that consume significant credits,
                        allowing you to make informed decisions.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Purchasing Credits</h3>
                      <div className="space-y-3">
                        <p className="text-sm text-gray-700">
                          To purchase more credits:
                        </p>
                        <ol className="text-sm text-gray-700 list-decimal list-inside ml-4 space-y-2">
                          <li>Click on your credit balance in the top right corner of the header</li>
                          <li>Click the "Purchase Credits" button in the credits modal</li>
                          <li>Select a credit package that fits your needs</li>
                          <li>Complete the secure checkout process</li>
                          <li>Credits are added to your account immediately upon successful payment</li>
                        </ol>
                      </div>
                    </div>

                    <Separator />

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-green-900 mb-1">Credit Usage Tips</h4>
                          <ul className="text-sm text-green-800 space-y-1 mt-2">
                            <li>• Be specific in your queries to get better results with fewer follow-up questions</li>
                            <li>• Use @mentions to focus AI responses on specific documents</li>
                            <li>• Review task results history to avoid re-running the same analysis</li>
                            <li>• Monitor your credit balance regularly to avoid interruptions</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'privacy' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        <ShieldCheck className="inline w-6 h-6 mr-2 mb-1 text-emerald-600" />
                        Privacy & Confidentiality
                      </h2>
                      <p className="text-gray-700">
                        Patent Loop is committed to protecting your intellectual property and keeping all of your data confidential.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Data Is Never Used for AI Training</h3>
                      <p className="text-sm text-gray-700 mb-3">
                        All documents, messages, queries, and information you provide to Patent Loop are <strong>never used to train AI models</strong>. Your data is processed solely to deliver analysis results to you and is not shared with any third-party AI providers for training purposes.
                      </p>
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-emerald-900 mb-1">AI Provider Guarantees</h4>
                            <p className="text-sm text-emerald-800">
                              Patent Loop uses enterprise-tier AI services from OpenAI and Anthropic that contractually guarantee your data is not used for model training. Your patent documents, prosecution strategies, and communications remain yours alone.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Confidentiality of Your Information</h3>
                      <p className="text-sm text-gray-700 mb-3">
                        All information associated with your account is treated as strictly confidential. This includes:
                      </p>
                      <div className="space-y-2">
                        <div className="border-l-4 border-emerald-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Patent Documents & Uploads</div>
                          <div className="text-sm text-gray-600">All patents, applications, and files you upload are stored securely and are accessible only to you.</div>
                        </div>
                        <div className="border-l-4 border-emerald-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Chat Messages & Analysis Results</div>
                          <div className="text-sm text-gray-600">Your conversations and AI-generated analysis are private to your account.</div>
                        </div>
                        <div className="border-l-4 border-emerald-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Office Action Strategies</div>
                          <div className="text-sm text-gray-600">Prosecution strategies, response drafts, and reporting emails are kept confidential.</div>
                        </div>
                        <div className="border-l-4 border-emerald-500 pl-3 py-1">
                          <div className="font-medium text-gray-900">Account Information</div>
                          <div className="text-sm text-gray-600">Your personal and account details are never shared with third parties.</div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">How Feedback Is Used</h3>
                      <p className="text-sm text-gray-700 mb-3">
                        Any feedback or usage data collected is used <strong>exclusively by Patent Loop personnel</strong> for the sole purpose of improving the application. This data is never shared externally, sold, or used for any other purpose.
                      </p>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-blue-900 mb-1">In Summary</h4>
                            <ul className="text-sm text-blue-800 space-y-1 mt-2">
                              <li>Your data is <strong>never</strong> used for AI model training</li>
                              <li>All documents and messages are strictly confidential</li>
                              <li>Feedback is only used by Patent Loop personnel to improve the application</li>
                              <li>No data is shared with or sold to third parties</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t p-4 bg-gray-50 flex-shrink-0">
              <div className="flex justify-end">
                <Button onClick={() => onOpenChange(false)}>
                  Close Guide
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
